import React from 'react';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import './SitesCardView.scss';
import { WhisperComponent } from '../../developers/page-components/WhisperComponent';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { ISitesProps } from '../../../views/merchants/details/MerchantSitesPage';
import { BUTTON_TYPE, ButtonComponent } from '../../ButtonComponent';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';
import { useNavigate, useLocation } from 'react-router-dom';
import { SITE_ROUTES } from '../../../constants/developer/site-constants/site-constants';
import { CopyComponent } from '../../CopyComponent';

interface ISiteCardFieldsProps {
	key: string,
	title: string,
	text: string,
	additionalContainerClass?: string,
	additionalTitleClass?: string,
	additionalTextClass?: string,
	isTooltip?: boolean,
	canCopy?: boolean,
}

const siteCardFields = (siteProps: ISitesProps): ISiteCardFieldsProps[] => [
	{
		key: siteProps?.id,
		title: 'Site Id',
		text: siteProps?.id,
		additionalTitleClass: 'col-4',
		additionalTextClass: 'col-8',
		isTooltip: true,
		canCopy: true,
	},
	{
		key: siteProps?.descriptor,
		title: 'Descriptor',
		text: siteProps?.descriptor,
		additionalTitleClass: 'col-4',
		additionalTextClass: 'col-8',
		isTooltip: true
	},
	{
		key: siteProps?.customerSupportEmail,
		title: 'Customer Support Email',
		text: siteProps?.customerSupportEmail,
		additionalTitleClass: 'col-6',
		additionalTextClass: 'col-6',
		isTooltip: true
	},
	{
		key: siteProps?.customerSupportPhone,
		title: 'Customer Support Phone',
		text: siteProps?.customerSupportPhone,
	},
	{
		key: siteProps?.customerSupportLink,
		title: 'Customer Support Url',
		text: siteProps?.customerSupportLink,
		additionalTitleClass: 'col-5',
		additionalTextClass: 'col-7',
		isTooltip: true
	},
];

interface ISitesCardViewProps {
	openModal: () => void;
	onEdit: (siteInfo: ISitesProps) => void;
	deleteSite: (id: string) => void;
	isAdmin?: boolean;
	isIntegrationPartner?: boolean;
}

export const SitesCardView = (props: ISitesCardViewProps): JSX.Element => {
	const { openModal, onEdit, deleteSite, isAdmin, isIntegrationPartner } = props;
	const { merchantState: { sitesList }} = useMerchants();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const handleEdit = (site): void => {
		if (isAdmin || isIntegrationPartner) {
			onEdit(site);
		} else {
			navigate(`${pathname}/${site?.id}/${SITE_ROUTES.SITE_INFO}`);
		}
	};

	const getSiteCardFields = (site): JSX.Element[] => (
		siteCardFields(site).map((el: ISiteCardFieldsProps) => (
			<div className={clsx('border-bottom pb-3 mb-5 d-flex align-items-center', el?.additionalContainerClass)} key={el.title}>
				<span className={clsx('text-muted fs-7',el?.additionalTitleClass)}>{el.title}:</span>
				{el?.canCopy && (
					<CopyComponent value={el?.text} title={el?.title} additionalClass="me-2"/>
				)}
				{
					el?.isTooltip
						? (
							<WhisperComponent
								whisperText={el.text}
								mainText={el.text}
								placement="top"
								additionalClass={clsx('fw-bolder fs-6 text-truncate', el?.additionalTextClass)}
							/>
						)
						:<span className="fs-6 fw-bolder ms-5">{el.text}</span>
				}
			</div>
		))
	);
	return (
		<div className="d-sm-flex flex-wrap site-card-container">
			{
				sitesList.map((site: ISitesProps) => (
					<div className="card site-card mt-5 ms-5" key={site?.id}>
						<div className="card-body">
							<div className="d-flex justify-content-between mb-5">
								<div className="fw-bolder fs-3">{site?.name}</div>
								{
									isAdmin && (
										<WhisperComponent
											whisperText="Delete site"
											mainText={
												<FontAwesomeIcon
													icon={faTrash}
													className="cursor-pointer site-card-trash"
													onClick={(): void => deleteSite(site?.id)}/>
											}
										/>
									)
								}
							</div>

							{getSiteCardFields(site)}

							{
								isAdmin && (
									<div className="border-bottom pb-3 mb-5 d-flex align-items-center justify-content-between">
										<span className="text-muted">Configurations:</span>
										<div className="d-flex align-items-center justify-content-between">
											<ButtonComponent
												buttonType={BUTTON_TYPE.WHITE}
												buttonName="Widget"
												onButtonClick={(): void => navigate(`${pathname}/${site?.id}/${MERCHANTS_TABS.WIDGET_CONFIGURATIONS}`)}
												additionalClass="me-3 px-3 py-2 no-letter-spacing"
											/>
											<ButtonComponent
												buttonType={BUTTON_TYPE.WHITE}
												buttonName="Checkout"
												onButtonClick={(): void => navigate(`${pathname}/${site?.id}/${MERCHANTS_TABS.CHECKOUT_CONFIGURATIONS}`)}
												additionalClass="me-3 px-3 py-2 no-letter-spacing"
											/>
											<ButtonComponent
												buttonType={BUTTON_TYPE.WHITE}
												buttonName="Sense JS"
												onButtonClick={(): void => navigate(`${pathname}/${site?.id}/${MERCHANTS_TABS.SENSE_JS}`)}
												additionalClass="px-3 py-2 no-letter-spacing"
											/>
										</div>
									</div>
								)
							}


							<div className="d-flex align-items-center justify-content-between">
								<span className="text-muted">WhiteListed Urls:</span>
								<span className="text-muted fs-5 fw-bolder ms-5  cursor-pointer" onClick={(): void => handleEdit(site)}>
									{isAdmin ? 'Edit' : 'View'}
								</span>
							</div>
						</div>
					</div>
				))
			}
			{
				isAdmin && (
					<div className="card site-card mt-5 ms-5 add-new-card cursor-pointer" onClick={openModal}>
						<div className="card-body d-flex align-items-center justify-content-center">
							<div className="fs-2 add-new-card-width">+ Add Site</div>
						</div>
					</div>
				)
			}
		</div>
	);
};
